import {StyleSheet} from 'react-native';
import CCColors from '@/Utils/CCColors';

// Reuses the Rewards screen's own card/heading/progress tokens (see
// src/Screens/Rewards/styles.ts) so this tab reads as a native part of the
// screen rather than a new visual system -- see docs/referral-program-plan.md
// (chess repo) for the design brief this was built from.
export const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'column',
    gap: 16,
    marginTop: 24,
  },
  card: {
    backgroundColor: CCColors.White,
    borderRadius: 8,
    padding: 24,
    shadowColor: CCColors.Primary.Dark,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.05,
  },
  cardMobile: {
    padding: 16,
  },
  banner: {
    borderRadius: 12,
    borderWidth: 2,
    borderColor: CCColors.Primary.Default,
    backgroundColor: CCColors.Red, // #E17846
    padding: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bannerMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 16,
    gap: 12,
  },
  bannerTitle: {
    fontWeight: '700',
  },
  bannerBody: {
    marginTop: 8,
    opacity: 0.9,
  },
  rewardInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FEF2B5',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  rewardInfoText: {
    fontSize: 13,
    color: CCColors.Primary.Dark,
    flexShrink: 1,
  },
  bannerIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerIconWrapMobile: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignSelf: 'flex-end',
  },
  cardHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },
  cardHeadingIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#EFE9FA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardHeadingText: {
    fontWeight: '700',
    fontSize: 18,
    color: CCColors.Primary.Dark,
  },
  helperText: {
    color: CCColors.TextColor.Grey,
    fontSize: 13,
    marginBottom: 16,
  },
  codeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  codeChip: {
    borderWidth: 1.5,
    borderColor: 'rgba(38,35,34,0.20)',
    borderStyle: 'dashed',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexGrow: 1,
  },
  codeText: {
    fontFamily: 'monospace',
    fontSize: 22,
    fontWeight: '700',
    color: CCColors.Primary.Dark,
  },
  copyHint: {
    color: CCColors.TextColor.Grey,
    fontSize: 12,
  },
  copyHintActive: {
    color: CCColors.Green,
  },
  linkPill: {
    backgroundColor: CCColors.Primary.Light,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexGrow: 1,
    minWidth: 160,
    maxWidth: '100%',
  },
  linkText: {
    color: CCColors.TextColor.Primary,
    fontSize: 13,
  },
  shareButton: {
    backgroundColor: CCColors.Primary.Default,
    borderRadius: 100,
    paddingVertical: 14,
    paddingHorizontal: 28,
  },
  shareButtonMobile: {
    flexGrow: 1,
    paddingHorizontal: 16,
  },
  shareButtonText: {
    color: CCColors.Primary.Dark,
    fontWeight: '700',
  },
  iconButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.20)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  whatsappButton: {
    backgroundColor: '#25D366',
    borderColor: '#25D366',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
    gap: 8,
  },
  emptyIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#EFE9FA',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.10)',
    gap: 12,
  },
  rowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  rowTopMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  rowBottomMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingLeft: 48,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: CCColors.Primary.Light,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontWeight: '700',
    fontSize: 13,
    color: CCColors.Primary.Dark,
  },
  rowNameCol: {
    flex: 1,
    minWidth: 120,
  },
  rowName: {
    fontWeight: '600',
    fontSize: 14,
    color: CCColors.Primary.Dark,
  },
  statusPill: {
    alignSelf: 'flex-start',
    borderRadius: 100,
    paddingVertical: 3,
    paddingHorizontal: 10,
    marginTop: 4,
  },
  statusPillText: {
    fontSize: 11,
    fontWeight: '700',
  },
  rewardedBadge: {
    borderRadius: 100,
    paddingVertical: 3,
    paddingHorizontal: 10,
    backgroundColor: '#FEF2B5',
    borderWidth: 1,
    borderColor: CCColors.Primary.Default,
    marginLeft: 8,
  },
  progressTrack: {
    width: 160,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(38,35,34,0.08)',
    overflow: 'hidden',
  },
  progressTrackMobile: {
    width: 120,
  },
  progressFill: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#D1AB41',
  },
  progressLabel: {
    fontSize: 12,
    color: CCColors.TextColor.Grey,
    marginTop: 4,
  },
  dateText: {
    fontSize: 12,
    color: CCColors.TextColor.Grey,
  },
  pagerRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
  },
  pagerButton: {
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.20)',
    borderRadius: 100,
    paddingVertical: 8,
    paddingHorizontal: 18,
  },
  pagerLabel: {
    fontSize: 13,
    color: '#888280',
  },
  addCodeRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
    flexWrap: 'wrap',
  },
  addCodeInput: {
    flexGrow: 1,
    minWidth: 160,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.20)',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 14,
    color: CCColors.TextColor.Primary,
  },
});
